import { Injectable, computed, effect, inject } from '@angular/core';
import { RELEASES, type ReleaseEntry } from '../constants/releases';
import { UserPreferencesService } from './user-preferences.service';
import { ProfileDataService } from './profile-data.service';
import { AnalyticsService } from './analytics.service';
import { ANALYTICS_EVENTS } from '../analytics/events';

/**
 * ReleasesService — read API for the What's New surface.
 *
 * RESPONSIBILITY
 *   Wraps the static RELEASES constant with three things the UI needs:
 *   (1) audience-filtered + sorted list, (2) per-user "unread" state
 *   keyed off UserPreferencesService.lastSeenReleaseId, (3) markAllSeen
 *   that bumps the seen-pointer to the latest entry.
 *
 * SIGNALS
 *   visibleReleases — Filtered (audience='admin' dropped for non-admins),
 *                     sorted newest-first by id (which is lex-sortable
 *                     by date due to the r-YYYY-MM-DD-slug convention).
 *   latestId        — Top-of-feed id, or null when the feed is empty.
 *   hasUnread       — True iff at least one visible entry is newer than
 *                     prefs.lastSeenReleaseId.
 *   unreadCount     — How many visible entries are unread.
 *
 * USED BY
 *   - paper-feedback-widget (pulsing dot on the floating trigger + the
 *     "What's new" CTA inside the open feedback panel)
 *   - im-whats-new-panel (renders visibleReleases, calls markAllSeen
 *     on mount)
 *   - command-registry (the "release notes" command)
 *
 * BACKEND CONTRACT
 *   None — content is in core/constants/releases.ts. When PMs want
 *   to author entries without a deploy, swap the constant for a
 *   GraphQL fetch; this service's signal contract stays identical.
 *
 * AI CONTEXT
 *   - The ID convention `r-<YYYY-MM-DD>-<slug>` is load-bearing for
 *     the unread check (`entry.id > lastSeenReleaseId`). Authors who
 *     break the convention will see backfilled entries silently fail
 *     to register as new.
 *   - markAllSeen is idempotent + a no-op when latestId === lastSeen.
 *   - Brand-new-user heuristic: when onboardingComplete flips false →
 *     true, lastSeenReleaseId is auto-seeded to the latest entry so
 *     the user doesn't see a stack of historical entries as "new".
 *     Seeding only fires once per session.
 */
@Injectable({ providedIn: 'root' })
export class ReleasesService {
  private readonly prefs = inject(UserPreferencesService);
  private readonly profile = inject(ProfileDataService);
  private readonly analytics = inject(AnalyticsService);

  /** Audience-filtered + newest-first list. */
  readonly visibleReleases = computed<ReleaseEntry[]>(() => {
    // Matches the adminGuard predicate — admin-audience release notes
    // surface for any viewer holding a concrete admin-class action
    // (`customer:manage`, `space:manage`, etc.), not just the rare
    // literal `'*'` / `'admin:*'` wildcard holders.
    const isAdmin = this.profile.canManageSomething();
    return [...RELEASES]
      .filter(r => r.audience !== 'admin' || isAdmin)
      .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  });

  readonly latestId = computed(() => this.visibleReleases()[0]?.id ?? null);

  readonly unreadCount = computed(() => {
    const lastSeen = this.prefs.lastSeenReleaseId();
    if (!lastSeen) return this.visibleReleases().length;
    return this.visibleReleases().filter(r => r.id > lastSeen).length;
  });

  readonly hasUnread = computed(() => this.unreadCount() > 0);

  /** Whether a single entry is unread relative to the seen pointer. */
  isUnread(entry: ReleaseEntry): boolean {
    const lastSeen = this.prefs.lastSeenReleaseId();
    if (!lastSeen) return true;
    return entry.id > lastSeen;
  }

  /** Idempotent — bumps lastSeenReleaseId to the top of the feed.
   *  Fires the WHATS_NEW_MARKED_SEEN event tagged with the trigger
   *  reason (used for funnel: panel-opened vs onboarding-self-seed). */
  markAllSeen(trigger: 'panel-opened' | 'onboarding-self-seed' | 'manual' = 'panel-opened'): void {
    const latest = this.latestId();
    if (!latest) return;
    if (this.prefs.lastSeenReleaseId() === latest) return;
    const cleared = this.unreadCount();
    this.prefs.update({ lastSeenReleaseId: latest });
    this.analytics.capture(ANALYTICS_EVENTS.WHATS_NEW_MARKED_SEEN, {
      latest_release_id: latest,
      entries_cleared: cleared,
      trigger,
    });
  }

  /** Tracks the previous onboardingComplete value so we only seed
   *  on the false → true transition (not on every signal read). */
  private onboardingPrev: boolean | null = null;

  constructor() {
    // Brand-new-user self-seed: when the user finishes the welcome
    // tour, fast-forward lastSeenReleaseId to the top of the feed so
    // they don't see every historical entry as "new". Only the
    // false → true transition triggers seeding; subsequent reads
    // (e.g. from a "reset onboarding" command) won't re-seed.
    effect(() => {
      const complete = this.prefs.onboardingComplete();
      const prev = this.onboardingPrev;
      this.onboardingPrev = complete;
      if (prev !== false) return; // skip the very first read or any non-transition
      if (!complete) return;
      // Seed only when the user has never seen anything yet — don't
      // overwrite a real seen pointer that an existing user already
      // has from a prior session. markAllSeen() handles the prefs
      // write + analytics emit; tagging the trigger separates this
      // cohort from "panel-opened" in the funnel.
      if (this.prefs.lastSeenReleaseId() != null) return;
      if (!this.latestId()) return;
      this.markAllSeen('onboarding-self-seed');
    });
  }
}
